import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {
	pagingOptions,
	labels,
	dateFormatsListItems,
	messagingDateFormat,
	dateFormats,
	successMessageDisplayDuration,
	currentResource
} from '../../../util/uiconstants';
import styled from 'styled-components';
import moment from 'moment';
import Loader from '../../Common/Loaders/loadingindicator';
import MotifTable from '@ey/motif-react-wrapper/Table';
import MotifTooltip from '@ey/motif-react-wrapper/Tooltip';
import MotifSearch from '@ey/motif-react-wrapper/Search';
import MotifFormField from '@ey/motif-react-wrapper/FormField';
import MotifButton from '@ey/motif-react-wrapper/Button';
import {MotifIcon, MotifIconButton} from '@ey-xd/motif-react';
import MotifToggleSwitch from '@ey/motif-react-wrapper/ToggleSwitch';
import Pagination from '../../Common/Pagination/PaginationMotif';
import env from '../../../util/env';
import {
	deleteMessage,
	getMessages,
	saveMessage,
	resetMessageDetails,
	editMessage
} from '../../../actions/admin/messagingactions';
import EllipsesControl from '../../Common/EllipsesControl/EllipsesControl';
import AccessDeniedMessage from '../../Common/AccessDeniedMessage/AccessDeniedMessage';
import ModalControl from '../../Common/Modal/Modal';
import CreateEditMessage from './createeditmessagemodal';
import DeleteMessage from './deletemessage';
import LocalToast from '../../Common/Toast/Toast';
import {getLocalizedAPIError, validateTicketNumber} from '../common/util';
import {IconoirTrash, IconoirEditPencil} from '@ey-xd/motif-icon';

function Messaging() {
	const messaging = useSelector((store) => store.messaging);
	const userPermissions = useSelector((store) => store.userPermissions);
	const dispatch = useDispatch();
	const history = useHistory();

	const [isEditMessage, setIsEditMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [optionPerPage, setOptionPerPage] = useState(pagingOptions.options[0]);
	const [totalPages, setTotalPages] = useState(0);
	const [searchInputValue, setSearchInputValue] = useState('');
	const [showCreateEditMessageModal, setShowCreateEditMessageModal] =
		useState(false);
	const [showDeleteMessageModal, setShowDeleteMessageModal] = useState(false);
	const [messageToDelete, setMessageToDelete] = useState(false);
	const [successMessage, setSuccessMessage] = useState(false);

	const duration = successMessageDisplayDuration;
	const [createEditMessageLoading, setCreateEditMessageLoading] =
		useState(false);
	const [buttonDisable, setButtonDisable] = useState(false);
	const [createEditErrorMessage, setCreateEditErrorMessage] = useState('');
	const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
	const [isDeleteLoading, setIsDeleteLoading] = useState(false);
	const [messageId, setMessageId] = useState(0);
	const [showExpired, setShowExpired] = useState(false);

	useEffect(() => {
		loadMessages(searchInputValue);
	}, [showExpired, page, optionPerPage]);

	useEffect(() => {
		if (messaging) {
			setTotalPages(messaging?.pages);
		}
	}, [messaging]);

	useEffect(() => {
		if (
			history.location &&
			history.location.hash.toLowerCase() == '#editmessage'
		) {
			var queryParams = new URLSearchParams(history.location.search);
			if (queryParams.has('messageid')) {
				setMessageId(parseInt(queryParams.get('messageid')));
				setIsEditMessage(true);
				setShowCreateEditMessageModal(true);
			}
		}
	}, [history.location]);

	useEffect(() => {
		if (successMessage) {
			var timer = setTimeout(() => {
				setSuccessMessage('');
			}, duration);

			return () => clearTimeout(timer);
		}
	}, [successMessage]);

	const loadMessages = (searchText) => {
		setIsLoading(true);
		let messageStatusParams = `messageStatus=current&messageStatus=scheduled${
			showExpired ? '&messageStatus=expired' : ''
		}`;
		let messageUrl =
			env.getURL('serviceUrlV2') +
			`admin/Messages?${messageStatusParams}&page=${page}&pageSize=${optionPerPage}`;

		if (searchText) {
			messageUrl += `&searchString=${searchText}`;
		}

		dispatch(getMessages(messageUrl))
			.then(() => {
				setIsLoading(false);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const openCreateMessageModal = () => {
		history.replace({
			pathname: history.location.pathname,
			hash: 'createmessage'
		});

		setShowCreateEditMessageModal(!showCreateEditMessageModal);
	};

	const saveMessageAPICall = (payload) => {
		setCreateEditMessageLoading(true);
		setButtonDisable(true);
		let saveMessageUrl = env.getURL('serviceUrlV2') + `admin/messages`;
		dispatch(saveMessage(saveMessageUrl, payload))
			.then(() => {
				setCreateEditMessageLoading(false);
				setButtonDisable(false);
				setSuccessMessage(labels.createMessageSuccess);
				closeCreateEditMessageModal(true);
			})
			.catch((error) => {
				setCreateEditErrorMessage(getLocalizedAPIError(error));
				setIsLoading(false);
			})
			.finally(() => {
				setCreateEditMessageLoading(false);
				setButtonDisable(false);
			});
	};

	const editMessageAPICall = (payload) => {
		setCreateEditMessageLoading(true);
		setButtonDisable(true);
		let editMessageUrl =
			env.getURL('serviceUrlV2') + `admin/messages/${messageId}`;
		dispatch(editMessage(editMessageUrl, payload))
			.then(() => {
				setCreateEditMessageLoading(false);
				setButtonDisable(false);
				setSuccessMessage(labels.editMessageSuccess);
				closeCreateEditMessageModal(true);
			})
			.catch((error) => {
				setCreateEditErrorMessage(getLocalizedAPIError(error));
				setIsLoading(false);
			})
			.finally(() => {
				setCreateEditMessageLoading(false);
				setButtonDisable(false);
			});
	};

	const onDeleteConfirmClick = (enteredTicketNumber) => {
		if (validateTicketNumber(enteredTicketNumber)) {
			setIsDeleteLoading(true);
			let deleteMessageUrl =
				env.getURL('serviceUrlV2') +
				'admin/messages/' +
				messageToDelete?.appAdminMessageId +
				'?ticketnumber=' +
				encodeURIComponent(enteredTicketNumber);
			dispatch(deleteMessage(deleteMessageUrl))
				.then(() => {
					setShowDeleteMessageModal(false);
					setSuccessMessage(labels.messagingModalDeletedSuccessfully);
					loadMessages();
				})
				.catch((error) => {
					let e = getLocalizedAPIError(error);
					setDeleteErrorMessage(e);
				})
				.finally(() => {
					setIsDeleteLoading(false);
				});
		}
	};

	const closeCreateEditMessageModal = (shallRefresh) => {
		setShowCreateEditMessageModal(false);
		setCreateEditErrorMessage('');
		setMessageId(0);
		setIsEditMessage(false);
		let path = history.location.pathname;
		if (history.location.hash && history.location.hash.length > 0) {
			history.push(path);
		}
		if (shallRefresh) {
			loadMessages(searchInputValue);
		}
		dispatch(resetMessageDetails());
	};

	const columnDefs = [
		{
			flex: 3,
			minWidth: 0,
			headerName: labels.messagingMessageTitle,
			field: 'messagetitle',
			cellRenderer: (params) => messageTitleRender(params),
			lockPosition: true,
			suppressSizeToFit: true
		},
		{
			flex: 2,
			minWidth: 0,
			headerName: labels.messagingStartsOn,
			field: 'startson',
			cellRenderer: (params) => startsOnRender(params),
			lockPosition: true,
			suppressSizeToFit: true
		},
		{
			flex: 2,
			minWidth: 0,
			headerName: labels.messagingEndsOn,
			field: 'endson',
			cellRenderer: (params) => endsOnRender(params),
			lockPosition: true,
			suppressSizeToFit: true
		},
		{
			flex: 2,
			minWidth: 0,
			headerName: labels.messagingAffectedDataCenters,
			field: 'affecteddatacenters',
			cellRenderer: (params) => affectedDataCentersRender(params),
			lockPosition: true,
			suppressSizeToFit: true
		},
		{
			flex: 2,
			minWidth: 0,
			headerName: labels.messagingDisplayIn,
			field: 'displayin',
			cellRenderer: (params) => displayInRender(params),
			lockPosition: true,
			suppressSizeToFit: true
		},
		{
			flex: 1,
			minWidth: 0,
			headerName: labels.messagingStatus,
			field: 'status',
			cellRenderer: (params) => statusRender(params),
			lockPosition: true,
			suppressSizeToFit: true
		},
		{
			flex: 1.5,
			minWidth: 0,
			headerName: labels.messagingCreatedBy,
			field: 'createdby',
			cellRenderer: (params) => createdByRender(params),
			lockPosition: true,
			suppressSizeToFit: true
		},
		{
			flex: 1.5,
			minWidth: 0,
			headerName: labels.messagingCreatedDate,
			field: 'createddate',
			cellRenderer: (params) => createdDateRender(params),
			lockPosition: true,
			suppressSizeToFit: true
		},
		{
			flex: 1.2,
			minWidth: 0,
			headerName: '',
			field: 'actions',
			cellRenderer: (params) => actionsRenderer(params),
			lockPosition: true,
			suppressSizeToFit: true
		}
	];

	const ellipsesControlRender = (text) => {
		return (
			<section className="roleType">
				<EllipsesControl
					content={text}
					tooltip={text}
					displayId={''}
					noOfLines={1}
					ellipsisClass="columnName"
				/>
			</section>
		);
	};

	const messageTitleRender = (e) => {
		return (
			<>
				<section className="roleType motif-body2-default-light">
					{ellipsesControlRender(e.data.messageTitle)}
				</section>
			</>
		);
	};

	const startsOnRender = (e) => {
		let startsOnDateTime = e.data.releaseOnDate
			? moment.utc(e.data.releaseOnDate).format(messagingDateFormat)
			: '';
		return (
			<section className="motif-body2-default-light">
				{ellipsesControlRender(startsOnDateTime)}
			</section>
		);
	};

	const endsOnRender = (e) => {
		let endsOnDateTime = e.data.expireOnDate
			? moment.utc(e.data.expireOnDate).format(messagingDateFormat)
			: '';
		return (
			<section className="motif-body2-default-light">
				{ellipsesControlRender(endsOnDateTime)}
			</section>
		);
	};

	const affectedDataCentersRender = (e) => {
		let affectedDataCenter = '';
		if (e.data.isSystemWide) {
			affectedDataCenter = labels.messagingDataCenterSystemWide;
		} else if (e.data.dataCenterList.length == 1) {
			affectedDataCenter = e.data.dataCenterList[0].dataCenterName;
		} else if (e.data.dataCenterList.length > 1) {
			affectedDataCenter = Array.prototype.map
				.call(e.data.dataCenterList, function (item) {
					return item.dataCenterName;
				})
				.join(', ');
		}

		return (
			<section className="motif-body2-default-light">
				{ellipsesControlRender(affectedDataCenter)}
			</section>
		);
	};

	const displayInRender = (e) => {
		let statusrender = <>{currentResource.messageType[e.data.messageTypeID]}</>;
		return (
			<section className="motif-body2-default-light">
				{ellipsesControlRender(statusrender)}
			</section>
		);
	};

	const statusRender = (e) => {
		let statusrender = <>{currentResource.messageStatus[e.data.statusId]}</>;
		return (
			<section className="motif-body2-default-light">
				{ellipsesControlRender(statusrender)}
			</section>
		);
	};

	const createdByRender = (e) => {
		return (
			<section className="motif-body2-default-light">
				{ellipsesControlRender(e.data.createUser)}
			</section>
		);
	};

	const createdDateRender = (e) => {
		let createDate = e.data.createDate
			? moment
					.utc(e.data.createDate)
					.format(dateFormatsListItems[dateFormats].label)
			: '';
		return (
			<section className="motif-body2-default-light">
				{ellipsesControlRender(createDate)}
			</section>
		);
	};

	const actionsRenderer = (e) => {
		return (
			<section className="iconbutton">
				<section className="editicon-button">
					<MotifTooltip
						id={'edit'}
						flip={false}
						hideCloseButton
						placement="left"
						contentClassName="editicon"
						hide={userPermissions?.permissions?.canCreateUpdateMessaging}
						trigger={
							<section>
								<MotifIconButton
									type="button"
									aria-label="edit-icon-btn"
									disabled={
										!userPermissions?.permissions?.canCreateUpdateMessaging
									}
									onClick={() => {
										history.replace({
											pathname: history.location.pathname,
											search:
												history.location.search.toLowerCase() +
												'?messageid=' +
												e.data.appAdminMessageId,
											hash: 'editmessage'
										});

										onEditClick(e.data);
									}}
									className="edit-user-edit-btn"
								>
									<MotifIcon
										name="edit"
										className="edit-icon"
										iconFunction={IconoirEditPencil}
										title={labels.editHoverText}
										size={24}
									/>
								</MotifIconButton>
							</section>
						}
					>
						{labels.insufficientPermission}
					</MotifTooltip>
				</section>
				<section className="deleteicon-button">
					<MotifTooltip
						id={'editbButtonTooltipId'}
						hideCloseButton
						placement="left"
						flip={false}
						contentClassName="deleteicon"
						hide={userPermissions?.permissions?.canDeleteMessaging}
						trigger={
							<section>
								<MotifIconButton
									type="button"
									aria-label="delete-icon"
									disabled={!userPermissions?.permissions?.canDeleteMessaging}
									onClick={() => {
										onDeleteMessageClick(e.data);
									}}
									className="delete-user-delete-btn"
								>
									<MotifIcon
										name="edit"
										className="edit-icon"
										iconFunction={IconoirTrash}
										title={labels.messagingModalDeleteButton}
										size={24}
									/>
								</MotifIconButton>
							</section>
						}
					>
						{labels.insufficientPermission}
					</MotifTooltip>
				</section>
			</section>
		);
	};

	const onDeleteMessageClick = (data) => {
		setShowDeleteMessageModal(true);
		setMessageToDelete(data);
	};

	const onEditClick = (data) => {
		setMessageId(data.appAdminMessageId);
		setIsEditMessage(true);
		setShowCreateEditMessageModal(!showCreateEditMessageModal);
	};

	const onSearchChange = () => {
		if (searchInputValue != undefined) {
			setPage(1);
			loadMessages(searchInputValue);
		}
	};

	const onClearClick = () => {
		setSearchInputValue('');
		loadMessages('');
	};

	const paginationHandler = (value) => {
		if (page !== value && page > 0) {
			setPage(value);
		}
	};
	const handlerOptionPage = (value) => {
		if (value !== optionPerPage) {
			setOptionPerPage(value);
			setPage(1);
		}
	};

	function hasAdminMessagePermissions() {
		let messagingPermission = userPermissions?.permissions;
		return (
			messagingPermission &&
			(messagingPermission.canReadMessaging ||
				messagingPermission.canCreateUpdateMessaging ||
				messagingPermission.canDeleteMessaging)
		);
	}

	const getRowStyle = (params) => {
		if (params.node.rowIndex % 2 === 0) {
			return {background: 'var(--neutrals-00white)'};
		} else {
			return {background: 'var(--ui-background-secondary)'};
		}
	};

	return (
		<Mainwrapper>
			{hasAdminMessagePermissions() ? (
				<section>
					<section className="messagingmanagement mainResponsiveWrapper">
						<section
							className="toastMessage"
							style={{
								display: successMessage.length > 0 ? 'block' : 'none'
							}}
						>
							<LocalToast
								variant="success"
								duration={duration}
								onClose={() => setSuccessMessage('')}
							>
								{successMessage}
							</LocalToast>
						</section>
						<section className="add-on">
							<section className="search">
								<MotifFormField>
									<MotifSearch
										placeholder={labels.searchUserText}
										value={searchInputValue}
										onChange={(e) => setSearchInputValue(e.target.value)}
										onSearchButtonClick={onSearchChange}
										onClear={onClearClick}
										onEnter={onSearchChange}
										clearHoverText={labels.clear}
									/>
								</MotifFormField>
							</section>
							<section className="showexpired">
								<MotifToggleSwitch
									aria-describedby="showmessage"
									checked={showExpired}
									onChange={(event) => {
										setShowExpired(event.target.checked);
										setPage(1);
									}}
									id="toggle1"
									offLabel=""
									onLabel={labels.showExpiredMessageslabel}
								/>
							</section>
							<MotifTooltip
								placement={'bottom'}
								hide={userPermissions?.permissions?.canCreateUpdateMessaging}
								trigger={
									<section className="createmessage">
										<MotifButton
											aria-label="Create message"
											variant="text-alt"
											disabled={
												!userPermissions?.permissions?.canCreateUpdateMessaging
											}
											onClick={() => {
												openCreateMessageModal();
											}}
										>
											{labels.createMessage}
										</MotifButton>
									</section>
								}
							>
								{labels.createMessagePermissions}
							</MotifTooltip>
						</section>
					</section>

					{isLoading ? (
						<Loader show view="blockView" styleName="loadingstyle" />
					) : messaging?.totalEntityCount > 0 ? (
						<section className="messagingmanagementTable mainResponsiveWrapper">
							<MotifTable
								defaultColDef={{
									flex: 1,
									sortable: false,
									filter: false,
									resizable: false,
									minWidth: 180,
									lockPosition: true
								}}
								getRowStyle={getRowStyle}
								enableCellTextSelection
								rowHeight={48}
								headerHeight={48}
								suppressCellSelection
								columnDefs={columnDefs}
								rowSelection="multiple"
								rowData={messaging?.data}
								rowBuffer={200}
								onGridReady={(params) => {
									params.api.sizeColumnsToFit();
								}}
							/>
						</section>
					) : (
						<section className="norecord">{labels.noResultsFound}</section>
					)}

					{!isLoading && messaging?.totalEntityCount > 0 && (
						<section className="pagerholder">
							<Pagination
								styleName="messaging-paging mainResponsiveWrapper"
								options={pagingOptions.options}
								pageSize={optionPerPage}
								showText={currentResource.common.pagination.show}
								onPageSizeChange={(pageSize) => {
									handlerOptionPage(pageSize);
								}}
								totalPages={totalPages}
								currentPage={page}
								onPageChange={(pageNumber) => {
									paginationHandler(pageNumber);
								}}
							/>
						</section>
					)}
					<section>
						{showCreateEditMessageModal === true && (
							<section>
								<ModalControl
									className="kendowrapper"
									modalsize="medium"
									role="dialog"
									modalContainerClass={'createMessage-modal'}
									title={
										isEditMessage ? labels.editMessage : labels.createMessage
									}
									show={showCreateEditMessageModal}
									showButtons={false}
									onHide={() => {
										closeCreateEditMessageModal(false);
									}}
								>
									<CreateEditMessage
										isEditMessage={isEditMessage}
										messageId={messageId}
										onCreateEditMessageModalClose={closeCreateEditMessageModal}
										onSaveMessageClick={saveMessageAPICall}
										onEditMessageAPICall={editMessageAPICall}
										createEditMessageLoading={createEditMessageLoading}
										buttonDisable={buttonDisable}
										setCreateEditErrorMessage={setCreateEditErrorMessage}
										createEditErrorMessage={createEditErrorMessage}
									/>
								</ModalControl>
							</section>
						)}
					</section>
					<section className="">
						<ModalControl
							className=""
							title={labels.messagingModalDeleteTitle}
							onOkClick={() => {}}
							onHide={() => {
								setShowDeleteMessageModal(false);
							}}
							isOkButtonDisabled={true}
							show={showDeleteMessageModal}
							modalsize="x-small-secondary"
							showButtons={false}
							closeTitle={labels.close}
							modalContainerClass="deleteMessage-modal"
							role="dialog"
							focusTrapActive={false}
						>
							<DeleteMessage
								messageToDelete={messageToDelete}
								onDeleteClick={onDeleteConfirmClick}
								onCancelClick={() => {
									setShowDeleteMessageModal(false);
								}}
								clearErrorMessage={() => {
									setDeleteErrorMessage('');
								}}
								errorMessage={deleteErrorMessage}
								isLoading={isDeleteLoading}
							/>
						</ModalControl>
					</section>
				</section>
			) : (
				<AccessDeniedMessage permissionMesage={labels.insufficientPermission} />
			)}
		</Mainwrapper>
	);
}

const Mainwrapper = styled.section`
	overflow: hidden;
	padding-top: var(--spacing-xx-large);
	.iconbutton {
		display: flex;
		gap: var(--spacing-large);
	}
	.editicon-button {
		display: flex;
		button.edit-user-edit-btn {
			display: flex;
			&[disabled] {
				opacity: 0.2;
				pointer-events: none;
			}
		}
	}

	.deleteicon-button {
		display: flex;
		button.delete-user-delete-btn {
			display: flex;
			&[disabled] {
				opacity: 0.2;
				pointer-events: none;
			}
		}
	}

	.loadingstyle {
		position: absolute;
		top: 50%;
		left: 50%;
	}

	//heading
	.messagingemanagement {
		.toastMessage {
			.motif-toast-text-container {
			}

			&.block {
				display: block;
			}

			&.none {
				display: none;
			}
		}
	}

	//addon

	.add-on {
		display: flex;
		margin-right: auto;
		padding-bottom: var(--spacing-x-large);

		.createmessage {
			display: flex;
			align-items: end;
		}

		.showexpired {
			width: auto;
			display: flex;
			margin-right: var(--spacing-x-large);
			align-items: end;
		}

		.search {
			margin-right: auto;
			.motif-form-field {
				width: var(--px-400);
			}
		}
	}

	//pagination

	.pagerholder {
		width: calc(100vw - var(--px-80));
		margin: 0 auto;
		max-width: 2048px;
		display: flex;
		padding: var(--px-24) 0;
	}

	.messaging-paging {
		justify-content: flex-end;
		line-height: normal;
		min-width: auto !important;
		font-size: var(--px-14);
		background: var(--neutrals-00white);
		border-top: 1px solid var(--neutrals-200);
	}

	.norecord {
		height: calc(100vh - var(--px-330));
		display: inline-block;
		width: 100%;
		display: flex;
		justify-content: center;
		flex: 0 auto;
		align-items: center;
		font-size: var(--px-16);
		color: var(--neutrals-700);
	}

	.messagingmanagementTable {
		overflow-y: auto;
		.motif-table {
			height: calc(100vh - var(--px-331)) !important;

			.ag-header-cell-text {
				font-size: var(--px-14) !important;
				font-weight: 400 !important;
				line-height: var(--px-20) !important;
				color: var(--neutrals-900);
			}

			.ag-header-cell .ag-header-select-all {
				visibility: hidden;
			}
			.ag-body {
				background: var(--neutrals-00white);
			}

			.ag-body-viewport {
				height: 100%;

				&:after {
					content: none;
				}
			}

			.ag-row,
			.ag-row-hover {
				border: none;
			}
			.ag-root.ag-layout-normal {
				overflow-y: auto;
				width: 100%;
			}

			.ag-root.ag-layout-auto-height {
				display: block;
			}

			.ag-center-cols-viewport {
				overflow-x: hidden;
			}

			.ag-cell-wrapper {
				display: grid;
				width: 100%;
			}
			.ag-header-cell:last-child,
			.ag-cell:last-child {
				display: flex;
				text-align: right;
				justify-content: center;
			}

			.roleType.disabled,
			.roleType.disabled * {
				color: var(--ui-text-disabled) !important;
				fill: none;
			}
			.noptr {
				pointer-events: none;
			}
			.disabled {
				color: var(--ui-text-disabled) !important;
			}

			.roleType {
				display: inline-block;
				width: 100%;
				align-items: center;

				.ellipses {
					margin: 0 !important;
					padding: 0 !important;
					display: inline-flex;
				}

				a {
					width: 100%;
					color: var(--blue-600);
					.ellipses {
						color: var(--blue-600);
					}
				}

				.motif-badge {
					width: var(--px-120);
				}

				&.enable.icon {
					fill: var(--neutrals-1000) !important;
				}
				.columnName {
					font-size: var(--px-14);
					color: var(--neutrals-900);
				}
			}

			.errorRow {
				color: var(--red-600) !important;
			}
		}

		.ag-body-horizontal-scroll {
			display: none;
		}
	}
`;

export default Messaging;
